import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { campaignApi, inspectionApi, type EvidenceClassificationEntryApi, type TreeEvidenceApi } from "@/lib/api";
import { hasPermission } from "@/lib/auth";
import { OPEN_INSPECTION_STATUSES, pickOpenInspection, uploadTreePhotos } from "@/lib/treePhotos";
import {
  PHOTO_CATEGORIES,
  PHOTO_CATEGORY_GROUPS,
  photoCategory,
  photoCategoryLabel,
  photoGroupLabel,
  type PhotoCategoryGroup,
} from "@/config/photo-categories";
import PhotoCategorySelect from "@/components/shared/PhotoCategorySelect";

interface Props {
  treeId: number | null;
  evidence: TreeEvidenceApi[];
  /** Chamado depois de um envio, para recarregar fotos e vistorias. */
  onPhotosAdded?: () => void;
}

/** Envio de fotos do exemplar: vao para a vistoria aberta ou abrem uma de monitoramento. */
function AddPhotosPanel({ treeId, onPhotosAdded }: { treeId: number; onPhotosAdded?: () => void }) {
  const [photoType, setPhotoType] = useState("GENERAL_TREE");
  const [openInspection, setOpenInspection] = useState<{ id: number; inspection_date: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [version, setVersion] = useState(0);
  const cameraInput = useRef<HTMLInputElement>(null);
  const filesInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    inspectionApi
      .listByTree(treeId)
      .then((rows) => active && setOpenInspection(pickOpenInspection(rows) ?? null))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [treeId, version]);

  const send = async (files: File[]) => {
    if (files.length === 0) return;
    setMessage(null);
    try {
      const { sent } = await uploadTreePhotos(treeId, files, {
        evidenceType: photoType,
        source: "CTA (ficha do exemplar)",
        onProgress: setBusy,
      });
      setMessage({ kind: "ok", text: `${sent} ${sent === 1 ? "foto enviada" : "fotos enviadas"}.` });
    } catch (err) {
      setMessage({ kind: "error", text: err instanceof Error ? `Não foi possível enviar: ${err.message}` : "Erro ao enviar." });
    } finally {
      setBusy(null);
      setVersion((v) => v + 1);
      onPhotosAdded?.();
    }
  };

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    void send(files);
  };

  return (
    <div className="mb-4 rounded-card border border-primary-200 bg-primary-50/40 p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-foreground-900">Adicionar fotos</p>
          <p className="text-xs text-foreground-500 mt-0.5">
            {openInspection
              ? `Vão para a vistoria aberta #${openInspection.id} (${fmtDate(openInspection.inspection_date)}).`
              : "Abre uma vistoria de monitoramento em rascunho e anexa as fotos a ela."}
          </p>
        </div>
        <label className="text-xs text-foreground-600">
          O que a foto mostra
          <PhotoCategorySelect
            value={photoType}
            onChange={setPhotoType}
            className="block mt-1 px-2.5 py-1.5 rounded-lg border border-background-200 text-xs bg-white"
          />
        </label>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => cameraInput.current?.click()}
          className="btn-primary text-xs disabled:opacity-50"
        >
          <i className="ri-camera-line mr-1"></i>Tirar foto
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => filesInput.current?.click()}
          className="btn-secondary text-xs disabled:opacity-50"
        >
          <i className="ri-image-add-line mr-1"></i>Escolher arquivos
        </button>
      </div>
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
      <input ref={filesInput} type="file" accept="image/*" multiple className="hidden" onChange={pick} />
      {busy && <p className="text-xs text-foreground-600 mt-2">{busy}</p>}
      {message && (
        <p className={`text-xs mt-2 ${message.kind === "ok" ? "text-primary-700" : "text-danger-700"}`}>{message.text}</p>
      )}
      {openInspection && (
        <Link to={`/cta/${treeId}/inspecoes/${openInspection.id}`} className="inline-block text-xs font-medium text-primary-700 mt-2">
          Completar e enviar a vistoria #{openInspection.id}
        </Link>
      )}
    </div>
  );
}


const STATUS_LABEL: Record<string, string> = {
  DRAFT: "rascunho",
  IN_PROGRESS: "em preenchimento",
  SUBMITTED: "enviada",
  UNDER_REVIEW: "em revisão",
  RETURNED: "devolvida",
  VALIDATED: "validada",
  CONCLUDED: "concluída",
};

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const kindOf = (mime: string | null) =>
  mime?.startsWith("video/") ? "video" : mime?.startsWith("audio/") ? "audio" : "image";

/** Carrega o arquivo com o token (a API é a única que serve evidência) quando aparece na tela. */
function AuthMedia({
  load,
  mime,
  alt,
  full,
}: {
  load: () => Promise<string | null>;
  mime: string | null;
  alt: string;
  full?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(!!full);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const kind = kindOf(mime);

  useEffect(() => {
    if (visible || !hostRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "120px" },
    );
    io.observe(hostRef.current);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    load()
      .then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        if (u) setUrl(u);
        else setFailed(true);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const placeholderIcon = kind === "video" ? "ri-video-line" : kind === "audio" ? "ri-mic-line" : "ri-image-line";

  return (
    <div ref={hostRef} className={full ? "w-full" : "w-full h-full"}>
      {!url ? (
        <div className={`flex flex-col items-center justify-center gap-1 bg-background-100 ${full ? "h-64" : "h-full"}`}>
          <i className={`${failed ? "ri-error-warning-line" : placeholderIcon} text-3xl text-foreground-300`}></i>
          {failed && <span className="text-[10px] text-foreground-400">Arquivo indisponível</span>}
        </div>
      ) : kind === "video" ? (
        <video src={url} controls={full} muted={!full} className={`w-full bg-black ${full ? "max-h-[60vh]" : "h-full object-cover"}`} />
      ) : kind === "audio" ? (
        <div className={`flex flex-col items-center justify-center gap-2 bg-background-100 px-2 ${full ? "h-40" : "h-full"}`}>
          <i className="ri-mic-line text-3xl text-primary-500"></i>
          <audio src={url} controls className="w-full" />
        </div>
      ) : (
        <img
          src={url}
          alt={alt}
          className={full ? "w-full max-h-[60vh] object-contain bg-background-100" : "w-full h-full object-cover"}
        />
      )}
    </div>
  );
}

const groupOf = (code: string | null | undefined): PhotoCategoryGroup | "NONE" =>
  photoCategory(code)?.group ?? "NONE";

const GROUP_BADGE: Record<PhotoCategoryGroup | "NONE", string> = {
  EXEMPLAR: "bg-primary-50 text-primary-800 border-primary-200",
  LOCAL: "bg-accent-50 text-accent-800 border-accent-200",
  OUTRO: "bg-background-100 text-foreground-600 border-background-200",
  NONE: "bg-warning-50 text-warning-800 border-warning-200",
};

function CategoryBadge({ code, className = "" }: { code: string | null; className?: string }) {
  const group = photoGroupLabel(code);
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${GROUP_BADGE[groupOf(code)]} ${className}`}>
      {group && group !== "Outro" ? `${group} · ` : ""}
      {photoCategoryLabel(code)}
    </span>
  );
}

/**
 * Classificacao da foto: o que ela mostra do exemplar ou do local.  Com a vistoria
 * aberta, quem envia fotos reclassifica; depois do envio, so quem homologa, com
 * motivo.  Toda troca fica no historico (autor, data, de/para).
 */
function ClassificationPanel({
  evidence,
  onChanged,
}: {
  evidence: TreeEvidenceApi;
  onChanged: (code: string) => void;
}) {
  const open = OPEN_INSPECTION_STATUSES.includes(evidence.workflow_status);
  const canEdit =
    !evidence.invalidated_at &&
    evidence.workflow_status !== "CANCELLED" &&
    (open ? hasPermission("evidence.upload") || hasPermission("inspection.homologate") : hasPermission("inspection.homologate"));
  const needsReason = !open;

  const [group, setGroup] = useState<PhotoCategoryGroup>(photoCategory(evidence.evidence_type)?.group ?? "EXEMPLAR");
  const [code, setCode] = useState(evidence.evidence_type ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [history, setHistory] = useState<EvidenceClassificationEntryApi[] | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;
    inspectionApi
      .evidenceClassificationHistory(evidence.inspection_id, evidence.evidence_id)
      .then((rows) => active && setHistory(rows))
      .catch(() => active && setHistory([]));
    return () => {
      active = false;
    };
  }, [evidence.inspection_id, evidence.evidence_id, version]);

  const options = PHOTO_CATEGORIES.filter((c) => c.group === group);
  const changed = code !== "" && code !== (evidence.evidence_type ?? "");
  const reasonOk = !needsReason || reason.trim().length >= 5;

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await inspectionApi.reclassifyEvidence(evidence.inspection_id, evidence.evidence_id, code, reason.trim() || undefined);
      setMessage({ kind: "ok", text: "Classificação registrada." });
      setReason("");
      setVersion((v) => v + 1);
      onChanged(code);
    } catch (err) {
      setMessage({ kind: "error", text: err instanceof Error ? err.message : "Não foi possível reclassificar." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-card border border-background-200 p-3 space-y-3" aria-label="Classificação da foto">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-foreground-900">Classificação</p>
        <CategoryBadge code={evidence.evidence_type} />
      </div>

      {canEdit ? (
        <>
          <div role="radiogroup" aria-label="A foto mostra" className="flex flex-wrap gap-2">
            {PHOTO_CATEGORY_GROUPS.map((g) => (
              <button
                key={g.code}
                type="button"
                role="radio"
                aria-checked={group === g.code}
                onClick={() => {
                  setGroup(g.code);
                  if (photoCategory(code)?.group !== g.code) {
                    setCode(PHOTO_CATEGORIES.find((c) => c.group === g.code)?.code ?? "");
                  }
                }}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium ${
                  group === g.code ? "bg-primary-600 text-white border-primary-600" : "bg-white text-foreground-700 border-background-200"
                }`}
              >
                <i className={`${g.icon} mr-1`} aria-hidden="true"></i>
                {g.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {options.map((c) => (
              <button
                key={c.code}
                type="button"
                aria-pressed={code === c.code}
                onClick={() => setCode(c.code)}
                className={`px-2.5 py-1 rounded-lg border text-xs ${
                  code === c.code
                    ? "border-primary-500 bg-primary-50 text-primary-800 font-medium"
                    : "border-background-200 text-foreground-700 hover:bg-background-50"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {changed && (
            <label className="block text-xs text-foreground-600">
              {needsReason ? "Motivo da reclassificação *" : "Motivo (opcional)"}
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="mt-1 w-full px-2.5 py-1.5 rounded-lg border border-background-200 text-xs"
                placeholder={needsReason ? "A vistoria já foi enviada: explique a correção." : ""}
              />
            </label>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" disabled={!changed || !reasonOk || busy} onClick={save} className="btn-primary text-xs disabled:opacity-50">
              {busy ? "Salvando..." : "Salvar classificação"}
            </button>
            {needsReason && (
              <span className="text-[11px] text-foreground-500">Vistoria já enviada: a troca exige motivo e fica registrada.</span>
            )}
          </div>
        </>
      ) : (
        <p className="text-xs text-foreground-500">
          {evidence.invalidated_at
            ? "Foto invalidada: a classificação não muda mais."
            : open
              ? "Seu perfil não reclassifica fotos."
              : "Vistoria já enviada: só quem homologa reclassifica as fotos."}
        </p>
      )}

      {message && <p className={`text-xs ${message.kind === "ok" ? "text-primary-700" : "text-danger-700"}`}>{message.text}</p>}

      {history && history.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-foreground-600 uppercase tracking-wide mb-1">Histórico da classificação</p>
          <ul className="space-y-1">
            {history.map((h) => (
              <li key={h.id} className="text-[11px] text-foreground-600">
                {fmtDate(h.changed_at)} · {h.changed_by_name ?? "Autor não registrado"}: {photoCategoryLabel(h.previous_type)} →{" "}
                <strong>{photoCategoryLabel(h.new_type)}</strong>
                {h.reason ? ` — ${h.reason}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

type GroupFilter = PhotoCategoryGroup | "NONE" | "";

const GROUP_FILTERS: { code: GroupFilter; label: string; icon: string }[] = [
  { code: "", label: "Todas", icon: "ri-gallery-line" },
  ...PHOTO_CATEGORY_GROUPS,
  { code: "NONE", label: "Sem categoria", icon: "ri-question-line" },
];

/**
 * Atalho no cartao: com a vistoria aberta troca a categoria direto; depois do
 * envio a troca exige motivo, entao o atalho abre o painel completo.
 */
function QuickCategory({
  evidence,
  onChanged,
  onOpen,
}: {
  evidence: TreeEvidenceApi;
  onChanged: (code: string) => void;
  onOpen: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (evidence.invalidated_at || evidence.workflow_status === "CANCELLED") return null;
  const open = OPEN_INSPECTION_STATUSES.includes(evidence.workflow_status);
  if (open && (hasPermission("evidence.upload") || hasPermission("inspection.homologate"))) {
    const change = async (code: string) => {
      if (!code || code === evidence.evidence_type) return;
      setBusy(true);
      setError(null);
      try {
        await inspectionApi.reclassifyEvidence(evidence.inspection_id, evidence.evidence_id, code);
        onChanged(code);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Não foi possível reclassificar.");
      } finally {
        setBusy(false);
      }
    };
    return (
      <div className="space-y-0.5">
        <PhotoCategorySelect
          value={evidence.evidence_type ?? ""}
          onChange={change}
          allowEmpty={evidence.evidence_type ? undefined : "Sem categoria"}
          ariaLabel={`Categoria da foto ${evidence.original_filename ?? evidence.evidence_id}`}
          className="w-full text-[11px] rounded-md border border-background-200 bg-white px-1.5 py-1 disabled:opacity-60"
        />
        {busy && <p className="text-[10px] text-foreground-500">Salvando...</p>}
        {error && <p role="alert" className="text-[10px] text-danger-700">{error}</p>}
      </div>
    );
  }
  if (!open && hasPermission("inspection.homologate")) {
    return (
      <button type="button" onClick={onOpen} className="text-[11px] font-medium text-primary-700 hover:underline">
        <i className="ri-price-tag-3-line mr-1" aria-hidden="true"></i>Reclassificar (exige motivo)
      </button>
    );
  }
  return null;
}

export default function TreePhotoGallery({ treeId, evidence, onPhotosAdded }: Props) {
  const [type, setType] = useState("");
  const [group, setGroup] = useState<GroupFilter>("");
  // Categoria trocada nesta tela, ate o recarregamento da lista chegar.
  const [overrides, setOverrides] = useState<Record<number, string>>({});
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showInvalid, setShowInvalid] = useState(false);
  const [selected, setSelected] = useState<TreeEvidenceApi | null>(null);
  const [hasBasePhoto, setHasBasePhoto] = useState(false);
  const [baseOpen, setBaseOpen] = useState(false);

  useEffect(() => {
    if (!treeId) return;
    let active = true;
    campaignApi
      .treePhotoMetadata(treeId)
      .then((meta) => active && setHasBasePhoto(Boolean(meta.has_photo)))
      .catch(() => active && setHasBasePhoto(false));
    return () => {
      active = false;
    };
  }, [treeId]);

  const items = useMemo(
    () => evidence.map((e) => (overrides[e.evidence_id] ? { ...e, evidence_type: overrides[e.evidence_id] } : e)),
    [evidence, overrides],
  );

  const types = useMemo(
    () =>
      (Array.from(new Set(items.map((e) => e.evidence_type).filter(Boolean))) as string[]).filter(
        (t) => !group || groupOf(t) === group,
      ),
    [items, group],
  );

  const groupCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const e of items) {
      if (!showInvalid && e.invalidated_at) continue;
      const g = groupOf(e.evidence_type);
      out[g] = (out[g] ?? 0) + 1;
    }
    return out;
  }, [items, showInvalid]);

  const filtered = useMemo(
    () =>
      items.filter((e) => {
        if (!showInvalid && e.invalidated_at) return false;
        if (group && groupOf(e.evidence_type) !== group) return false;
        if (type && e.evidence_type !== type) return false;
        const day = (e.captured_at ?? e.uploaded_at).slice(0, 10);
        if (from && day < from) return false;
        if (to && day > to) return false;
        return true;
      }),
    [items, group, type, from, to, showInvalid],
  );

  const invalidCount = evidence.filter((e) => e.invalidated_at).length;
  const inputCls = "px-2.5 py-1.5 rounded-lg border border-background-200 text-xs bg-white";

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-foreground-900 font-heading flex items-center gap-2">
          <i className="ri-camera-line text-primary-500"></i>
          Fotos e evidências
        </h3>
        <p className="text-xs text-foreground-500 mt-1">
          Cada evidência pertence à vistoria que a produziu e é exibida aqui por associação.
        </p>
      </div>

      {treeId && hasPermission("evidence.upload") && <AddPhotosPanel treeId={treeId} onPhotosAdded={onPhotosAdded} />}

      {/* Filtros: o que a foto mostra, categoria e período */}
      <div role="radiogroup" aria-label="Filtrar fotos por assunto" className="flex flex-wrap gap-2 mb-3">
        {GROUP_FILTERS.filter((g) => g.code === "" || (groupCounts[g.code] ?? 0) > 0).map((g) => (
          <button
            key={g.code || "ALL"}
            type="button"
            role="radio"
            aria-checked={group === g.code}
            onClick={() => {
              setGroup(g.code);
              setType("");
            }}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium ${
              group === g.code ? "bg-primary-600 text-white border-primary-600" : "bg-white text-foreground-700 border-background-200"
            }`}
          >
            <i className={`${g.icon} mr-1`} aria-hidden="true"></i>
            {g.label}
            {g.code !== "" && <span className="ml-1 opacity-75">({groupCounts[g.code] ?? 0})</span>}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-foreground-600">
          Categoria
          <select value={type} onChange={(e) => setType(e.target.value)} className={`${inputCls} block mt-1`}>
            <option value="">Todos</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {photoCategoryLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-foreground-600">
          De
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} block mt-1`} />
        </label>
        <label className="text-xs text-foreground-600">
          Até
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} block mt-1`} />
        </label>
        {invalidCount > 0 && (
          <label className="text-xs text-foreground-600 flex items-center gap-1.5 pb-1.5">
            <input type="checkbox" checked={showInvalid} onChange={(e) => setShowInvalid(e.target.checked)} />
            Mostrar invalidadas ({invalidCount})
          </label>
        )}
        {(group || type || from || to) && (
          <button
            type="button"
            onClick={() => {
              setGroup("");
              setType("");
              setFrom("");
              setTo("");
            }}
            className="text-xs text-primary-700 pb-1.5"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {hasBasePhoto && treeId && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setBaseOpen(true)}
            className="flex items-center gap-3 rounded-card border border-background-200/70 bg-white p-2 pr-4 hover:border-primary-300"
          >
            <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
              <AuthMedia load={() => campaignApi.treePhotoUrl(treeId)} mime="image/jpeg" alt="Foto-base do cadastro" />
            </div>
            <span className="text-left">
              <span className="block text-sm font-medium text-foreground-900">Foto-base do cadastro</span>
              <span className="block text-xs text-foreground-500">Registrada com o exemplar (FIQ-1)</span>
            </span>
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-foreground-500 text-sm">
          <i className="ri-image-line text-3xl block mb-2 opacity-30"></i>
          {evidence.length === 0
            ? "Nenhuma evidência registrada para este exemplar."
            : "Nenhuma evidência neste filtro."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((ev) => {
            const invalid = !!ev.invalidated_at;
            return (
              <div
                key={ev.evidence_id}
                className={`text-left bg-white rounded-card border overflow-hidden transition-colors ${
                  invalid ? "border-danger-200 opacity-70" : "border-background-200/70 hover:border-accent-300"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setSelected(ev)}
                  aria-label={`Ampliar foto ${ev.original_filename ?? ev.evidence_id}`}
                  className="relative block w-full h-40 bg-background-100"
                >
                  <AuthMedia
                    load={() => inspectionApi.evidenceFileUrl(ev.inspection_id, ev.evidence_id)}
                    mime={ev.mime_type}
                    alt={ev.description ?? "Evidência"}
                  />
                  <CategoryBadge code={ev.evidence_type} className="absolute top-2 right-2 shadow-sm" />
                </button>
                <div className="p-2.5 space-y-1">
                  <QuickCategory
                    evidence={ev}
                    onOpen={() => setSelected(ev)}
                    onChanged={(code) => {
                      setOverrides((o) => ({ ...o, [ev.evidence_id]: code }));
                      onPhotosAdded?.();
                    }}
                  />
                  {ev.description && <p className="text-xs text-foreground-800 leading-tight line-clamp-2">{ev.description}</p>}
                  <p className="text-[10px] text-foreground-500">
                    {fmtDate(ev.captured_at ?? ev.uploaded_at)} · vistoria #{ev.inspection_id} ({STATUS_LABEL[ev.workflow_status] ?? ev.workflow_status})
                  </p>
                  {invalid && <p className="text-[10px] font-semibold text-danger-700">Invalidada</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Evidência ampliada */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-card max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <AuthMedia
                full
                load={() => inspectionApi.evidenceFileUrl(selected.inspection_id, selected.evidence_id)}
                mime={selected.mime_type}
                alt={selected.description ?? "Evidência"}
              />
              <button
                onClick={() => setSelected(null)}
                aria-label="Fechar"
                className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center hover:bg-white text-foreground-700"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-4 space-y-2">
              {selected.description && <p className="text-sm text-foreground-900 font-medium">{selected.description}</p>}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                <span><i className="ri-calendar-line mr-1"></i>{fmtDate(selected.captured_at ?? selected.uploaded_at)}</span>
                <span><i className="ri-user-line mr-1"></i>{selected.uploaded_by_name ?? "Autor não registrado"}</span>
                <CategoryBadge code={selected.evidence_type} />
              </div>
              <p className="text-xs text-foreground-500">
                Origem:{" "}
                {treeId ? (
                  <Link to={`/cta/${treeId}/inspecoes/${selected.inspection_id}`} className="text-primary-700 font-medium">
                    vistoria #{selected.inspection_id}
                  </Link>
                ) : (
                  `vistoria #${selected.inspection_id}`
                )}{" "}
                ({STATUS_LABEL[selected.workflow_status] ?? selected.workflow_status})
              </p>
              {selected.invalidated_at && (
                <p className="text-xs rounded-lg bg-danger-50 border border-danger-200 text-danger-800 px-3 py-2">
                  <strong>Invalidada em {fmtDate(selected.invalidated_at)}.</strong> {selected.invalidation_reason}
                </p>
              )}
              <ClassificationPanel
                key={selected.evidence_id}
                evidence={selected}
                onChanged={(code) => {
                  setOverrides((o) => ({ ...o, [selected.evidence_id]: code }));
                  setSelected({ ...selected, evidence_type: code });
                  onPhotosAdded?.();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {baseOpen && treeId && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setBaseOpen(false)}>
          <div className="bg-white rounded-card max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <AuthMedia full load={() => campaignApi.treePhotoUrl(treeId)} mime="image/jpeg" alt="Foto-base do cadastro" />
            <p className="p-3 text-xs text-foreground-500">Foto-base registrada no cadastro do exemplar (FIQ-1).</p>
          </div>
        </div>
      )}
    </div>
  );
}
